import React, { useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box } from '@mui/material';

import Button from '@/components/Buttons/Button';
import Typography from '@/components/Typography';
import PremiumFeatureMessage from '@/components/UpgradeToPremium/PremiumFeatureMessage';
import { Permission } from '@/entities/permissions';
import { APP_CATALOG } from '@/features/Dashboard/apps';
import type { AppCatalogEntry } from '@/features/Dashboard/apps';
import { Category } from '@/features/Dashboard/apps/types';
import SelectWidget from '@/features/Dashboard/components/SelectWidget';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import {
  clearSelection,
  SELECTOR_MODES,
  setPendingAppSymbols
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import usePermission from '@/shared/hooks/usePermission';

const categories = [
  { key: Category.All, label: 'All apps' },
  { key: Category.Markets, label: 'Markets' },
  { key: Category.Charts, label: 'Charts' },
  { key: Category.Data, label: 'Data' }
];

// Incompatibility sets derived from APP_CATALOG
const SPOT_INCOMPATIBLE_IDS = new Set(
  APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('spot')).map(a => a.id)
);
const FWD_INCOMPATIBLE_IDS = new Set(
  APP_CATALOG.filter(a => (a.incompatibleWith as string[]).includes('fwd')).map(a => a.id)
);

export const AppSelector = () => {
  const { setDrawerStep, closeDrawer } = useDrawerSteps();
  const [category, setCategory] = useState<Category>(Category.All);
  const dispatch = useAppDispatch();
  const selectorMode = useAppSelector(s => s.symbolSelector.selectorMode);
  const selectorSymbols = useAppSelector(s => s.symbolSelector.symbols);

  const { hasPermission } = usePermission();

  // "From selector" mode: the drawer was opened via the "Create app" button
  const isFromSelector = selectorSymbols.length > 0;

  const filteredApps = useMemo(() => {
    if (category === Category.All) {
      return APP_CATALOG;
    }
    return APP_CATALOG.filter(app => app.category === category);
  }, [category]);

  const isAppDisabledByMode = useMemo(() => {
    if (!isFromSelector) return (_id: string) => false;
    if (selectorMode === SELECTOR_MODES.SPOT) {
      return (id: string) => SPOT_INCOMPATIBLE_IDS.has(id);
    }
    if (selectorMode === SELECTOR_MODES.FORWARD_CURVE) {
      return (id: string) => FWD_INCOMPATIBLE_IDS.has(id);
    }
    return (_id: string) => false;
  }, [isFromSelector, selectorMode]);

  const [availableApps, premiumOnlyApps] = useMemo(() => {
    if (hasPermission(Permission.DashboardPremiumWidgets)) {
      return [filteredApps, [] as AppCatalogEntry[]];
    }
    const nonPremium = filteredApps.filter(app => !app.premium);
    const premium = filteredApps.filter(app => app.premium);
    return [nonPremium, premium];
  }, [filteredApps, hasPermission]);

  const handleSelectApp = (app: AppCatalogEntry) => {
    if (isFromSelector) {
      dispatch(setPendingAppSymbols(selectorSymbols));
      dispatch(clearSelection());
    }
    setDrawerStep(app.step);
  };

  return (
    <>
      <DrawerHeader
        before={
          <DrawerButton
            icon={<ArrowBackIcon />}
            title='Go back'
            variant='secondary'
            onClick={closeDrawer}
          />
        }
        buttons={null}
        title='Add new app'
      />
      <DrawerBody>
        <Typography
          size={24}
          sx={{ fontWeight: 'bold' }}
        >
          Choose app type
        </Typography>
        <Typography
          color='textDisabled'
          size={14}
          sx={{
            mb: 3
          }}
        >
          Select an app to start configuring
        </Typography>
        <Box sx={{ display: 'grid', gap: 1, mb: 3, gridTemplateColumns: 'repeat(4,1fr)' }}>
          {categories.map(({ key, label }) => (
            <Button
              key={key}
              color={category === key ? 'primary' : 'secondary'}
              size='small'
              sx={{
                width: 'auto',
                maxWidth: '100%',
                height: 36
              }}
              variant='contained'
              onClick={() => {
                setCategory(key);
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {premiumOnlyApps.length > 0 && (
            <Box sx={{ position: 'relative', overflow: 'hidden', userSelect: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  position: 'relative',
                  filter: 'blur(3px)'
                }}
              >
                {premiumOnlyApps.map(app => (
                  <SelectWidget
                    key={app.id}
                    badge={app.badge}
                    description={app.description}
                    icon={app.icon}
                    soon={app.soon}
                    testId={`select-widget-${app.id}`}
                    title={app.title}
                    onSelect={() => {}}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  background: 'rgba(255, 255, 255, 0.05)',
                  cursor: 'default',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  color: '#FFF'
                }}
              >
                <PremiumFeatureMessage />
              </Box>
            </Box>
          )}
          {availableApps.map(app => (
            <SelectWidget
              key={app.id}
              badge={app.badge}
              description={app.description}
              disabled={isAppDisabledByMode(app.id)}
              icon={app.icon}
              soon={app.soon}
              testId={`select-widget-${app.id}`}
              title={app.title}
              onSelect={() => {
                handleSelectApp(app);
              }}
            />
          ))}
        </Box>
      </DrawerBody>
    </>
  );
};
